// ---------- la page Réglages ----------
// Un écran à part entière, ouvert par l'engrenage de l'accueil, et non un panneau : il y aura
// d'autres choses à régler (comme dans Codigrid : son, notifications, langue, choix
// publicitaires…), et une page se lit et se fait défiler mieux qu'une boîte par-dessus le chemin.
// Trois rubriques : le jeu, la sauvegarde, à propos. Chaque ligne est un bouton (ou un
// interrupteur) de toute la largeur, assez haut pour le pouce.

import { deleteAccount } from './account';
import { resetCoach } from './coach';
import { t, type I18nKey } from './i18n';
import { setSetting, settings, type KeyboardLayout } from './prefs';
import { openAccount } from './profile';
import { openStoreListing } from './review';
import { stopSync } from './sync';
import { $, esc, ICON, toast } from './ui';

export interface SettingsHooks {
  back(): void;
  /** Après une reprise de progression : l'accueil se redessine au retour. */
  restored(): void;
}

let hooks: SettingsHooks;
let wired = false;

declare const __APP_VERSION__: string | undefined;
const version = typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : '';

const toggle = (key: 'haptics' | 'motion', label: I18nKey, hint: I18nKey): string =>
  `<button class="set-row" type="button" role="switch" aria-checked="${settings()[key]}" data-set="${key}">
    <span class="set-txt"><b>${t(label)}</b><small>${t(hint)}</small></span>
    <span class="switch" aria-hidden="true"><i></i></span>
  </button>`;

const action = (act: string, label: I18nKey, hint: I18nKey, danger = false): string =>
  `<button class="set-row${danger ? ' danger' : ''}" type="button" data-act="${act}">
    <span class="set-txt"><b>${t(label)}</b><small>${t(hint)}</small></span>
    <span class="chev" aria-hidden="true">${ICON.next}</span>
  </button>`;

function render(): void {
  const kb = settings().keyboard;
  $('settings').innerHTML = `
    <header class="topbar set-bar">
      <button class="icon-btn" type="button" data-act="back" aria-label="${t('settings.back')}">${ICON.prev}</button>
      <div class="brand"><b>${t('settings.title')}</b></div>
    </header>
    <div class="set-body">
      <h2>${t('settings.game')}</h2>
      <div class="set-card">
        ${toggle('haptics', 'settings.haptics', 'settings.hapticsHint')}
        ${toggle('motion', 'settings.motion', 'settings.motionHint')}
        <div class="set-row static">
          <span class="set-txt"><b>${t('settings.keyboard')}</b><small>${t('settings.keyboardHint')}</small></span>
          <span class="seg" role="radiogroup" aria-label="${t('settings.keyboard')}">
            ${(['azerty', 'abc'] as KeyboardLayout[]).map((k) => `<button type="button" role="radio" aria-checked="${kb === k}" data-kb="${k}">${k.toUpperCase()}</button>`).join('')}
          </span>
        </div>
        ${action('coach', 'settings.coach', 'settings.coachHint')}
      </div>
      <h2>${t('settings.save')}</h2>
      <div class="set-card">
        ${action('account', 'settings.recovery', 'settings.recoveryHint')}
        ${action('wipe', 'settings.wipe', 'settings.wipeHint', true)}
      </div>
      <h2>${t('settings.about')}</h2>
      <div class="set-card">
        ${action('rate', 'settings.rate', 'settings.rateHint')}
        <div class="set-row static"><span class="set-txt"><b>Verbigrid</b><small>${esc(version ? t('settings.version', { v: version }) : t('settings.tagline'))}</small></span></div>
      </div>
    </div>`;
}

export function openSettings(h: SettingsHooks): void {
  hooks = h;
  render();
  if (wired) return;
  wired = true;
  $('settings').addEventListener('click', (e) => {
    const el = (e.target as HTMLElement).closest<HTMLElement>('[data-act], [data-set], [data-kb]');
    if (!el) return;
    if (el.dataset.set) {
      const key = el.dataset.set as 'haptics' | 'motion';
      setSetting(key, !settings()[key]);
      el.setAttribute('aria-checked', String(settings()[key]));
      return;
    }
    if (el.dataset.kb) {
      setSetting('keyboard', el.dataset.kb as KeyboardLayout);
      return render();
    }
    switch (el.dataset.act) {
      case 'back':
        return hooks.back();
      case 'coach':
        resetCoach();
        return toast(t('settings.coachDone'));
      case 'account':
        return void openAccount(() => hooks.restored());
      case 'rate':
        return openStoreListing();
      case 'wipe':
        return void wipe();
    }
  });
}

/** Supprimer mes données : le compte sur le serveur d'abord, l'appareil ensuite. Si le serveur ne
 *  répond pas, on ne touche à rien — rien n'est pire qu'une suppression qu'on croit faite. */
async function wipe(): Promise<void> {
  if (!confirm(t('settings.wipeConfirm'))) return;
  // Plus rien ne part avant la suppression : un envoi en vol arriverait sinon après elle.
  stopSync();
  try {
    await deleteAccount();
  } catch {
    // Rien n'est supprimé : la synchro reprend au prochain lancement, et c'est ce qu'il faut.
    return toast(t('settings.wipeFailed'));
  }
  try {
    for (const k of Object.keys(localStorage))
      if (k.startsWith('verbigrid:')) localStorage.removeItem(k);
  } catch {
    /* stockage refusé : il n'y avait rien dedans */
  }
  location.reload();
}
